import { useState, useEffect } from "react";
import axios from "../customAxios.js";
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Button,
  Alert,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineDot,
  TimelineContent,
} from "@mui/lab";
import { useParams } from "react-router-dom";
import {
  get_order_type_label,
  get_order_shipping_method_label,
  get_order_provider,
} from "../helpers/enums.js";
import { get_full_address } from "../helpers/addressParser.js";
import {
  strDateFormatWithTime,
  filterStatusByOrderType,
} from "../utils/functions";

function TrackOrder() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [loadingPageData, setLoadingPageData] = useState(true);
  const [podImages, setPodImages] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [whatsappContact, setWhatsappContact] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState(null);
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [adminCourierName, setAdminCourierName] = useState(null);
  const [loadingError, setLoadingError] = useState("");

  useEffect(() => {
    fetchOrder();
  }, []);

  useEffect(() => {}, [podImages]);

  useEffect(() => {
    // Update the WhatsApp link dynamically whenever contact or message changes
    if (whatsappContact && whatsappMessage) {
      setWhatsappLink(
        `https://wa.me/${whatsappContact}?text=${encodeURIComponent(
          whatsappMessage
        )}`
      );
    }
  }, [whatsappContact, whatsappMessage]);

  // functions:

  const fetchOrder = async () => {
    if (!loadingPageData) {
      setLoadingPageData(true);
    }

    try {
      const { data } = await axios.get(
        `exposed_api/customer_track_order/${orderId}/`
      );
      if (data?.serialized_order) {
        setOrder(data.serialized_order);
        setPodImages(data.serialized_order);
        setLoadingError("");
        if (data.serialized_admin && data.serialized_admin.user) {
          setAdminCourierName(
            `${data.serialized_admin.user.first_name}  ${data.serialized_admin.user.last_name}`
          );
        }

        setFilteredStatuses(filterStatusByOrderType(data.serialized_order));

        // set whatsapp:
        if (data.serialized_order.warehouse?.customer_phone) {
          setWhatsappContact(data.serialized_order.warehouse.customer_phone);
          setWhatsappMessage(
            `Hola, necesito ayuda con mi orden #${orderId} de ${data.serialized_order.seller.company.social_reason}`
          );
        } else {
          setWhatsappContact("56940062756");
          setWhatsappMessage(`Hola, necesito ayuda con mi orden #${orderId}`);
        }
      } else {
        setLoadingError("Error al cargar la orden.");
      }
    } catch (error) {
      setLoadingError("Hubo un error al cargar los datos.");
      console.error(`Hubo un error al cargar los datos: ${error}`);
    } finally {
      setLoadingPageData(false);
    }
  };

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      alert("Copied to clipboard");
    });
  }

  // Handlers:

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage(null);
  };

  return loadingError !== "" ? (
    <Alert severity="error">{loadingError}</Alert>
  ) : loadingPageData || !order ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "top",
        height: "100vh", // Full viewport height
      }}
    >
      <CircularProgress size={100} />
    </Box>
  ) : (
    // NEW
    <div>
      <div className="split-container-tracking">
        <div className="green-header-tracking" align="center">
          {typeof adminCourierName === "string" &&
          adminCourierName.trim() !== "" ? (
            <h4 className="title-login fw-medium">
              Pedido gestionado por {adminCourierName}
            </h4>
          ) : (
            <img
              src="https://static.wixstatic.com/media/d57788_b9b38e53004a474b81cad819df75dc60~mv2.png"
              width="400"
              alt="Logo NinjaHubs"
              className="centerImageTracking"
            />
          )}
        </div>

        <div className="white-section-tracking">
          <div className="tracking-card" align="left">
            <h4 className="title-login fw-medium">
              Orden #{orderId} de {order?.seller?.company?.social_reason || ""}
            </h4>
            {/* form antigui */}
            <Grid item xs={7}>
              {/* Información de Productos */}
              <Grid
                container
                rowSpacing={2}
                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
              >
                <Grid item xs={12}></Grid>

                {/* Información del Destinatario */}
                <Grid item xs={12}>
                  <Card variant="none">
                    <CardContent>
                      <Grid
                        container
                        rowSpacing={1}
                        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                      >
                        <Grid item xs={5}>
                          <Typography className="fw-medium">
                            Referencia
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography>
                            {order.reference_id}
                          </Typography>
                        </Grid>
                        <Grid item xs={5}>
                          <Typography className="fw-medium">
                            Tipo de Orden
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography>
                            {get_order_type_label(order.type)}
                          </Typography>
                        </Grid>
                        {(order.type === "delivery" ||
                          order.type === "delivery_fulfillment" ||
                          order.type === "pick_up" ||
                          order.type === "pick_up_fulfillment" ||
                          order.type === "delivery_return") && (
                          <>
                            <Grid item xs={5}>
                              <Typography className="fw-medium">
                                Destinatario
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography>{order.recipient}</Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography className="fw-medium">
                                Tipo de Entrega
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography>
                                {order.delivery?.shipping_method
                                  ? get_order_shipping_method_label(
                                      order.delivery?.shipping_method
                                    )
                                  : "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography className="fw-medium">
                                Dirección de Entrega
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography>
                                {order.delivery?.shipping_address
                                  ? get_full_address(
                                      order.delivery?.shipping_address
                                    )
                                  : "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography className="fw-medium">
                                Proveedor de la Entrega
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography>
                                {order.delivery
                                  ? get_order_provider(order.delivery)
                                  : "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography className="fw-medium">
                                Bultos
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography>
                                {order.delivery?.packages &&
                                order.delivery.packages.trim() !== ""
                                  ? order.delivery.packages
                                  : "1"}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography className="fw-medium">
                                URL tracking
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography>
                                {order.delivery?.url_tracking ? (
                                  <Button
                                    onClick={(event) => {
                                      event.preventDefault();
                                      window.open(
                                        order.delivery.url_tracking,
                                        "_blank",
                                        "noopener,noreferrer"
                                      ); // Opens the URL in a new tab
                                    }}
                                    variant="contained"
                                    sx={{
                                      backgroundColor: "#25D366", // WhatsApp green color
                                      color: "#FFFFFF", // White text
                                      textTransform: "none", // Keeps the text as "Tracking pedido" without uppercase
                                      padding: "8px 16px", // Adjust padding for button style
                                      borderRadius: "4px", // Rounds the corners slightly
                                      boxShadow: "none", // Removes extra shadow for a cleaner look
                                      "&:hover": {
                                        backgroundColor: "#1ebe5b", // Slightly darker green on hover
                                        boxShadow: "none", // Prevents hover shadow
                                      },
                                    }}
                                  >
                                    Tracking Courier
                                  </Button>
                                ) : (
                                  "-"
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography className="fw-medium">
                                Consultar por Whatsapp
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Button
                                component="a"
                                href={whatsappLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="contained"
                                sx={{
                                  backgroundColor: "#25D366", // WhatsApp green color
                                  color: "#FFFFFF", // White text
                                  textTransform: "none", // Keeps the text as "Consultas" without uppercase
                                  padding: "8px 16px", // Adjust padding for button style
                                  borderRadius: "4px", // Rounds the corners slightly
                                  boxShadow: "none", // Removes extra shadow for a cleaner look
                                  "&:hover": {
                                    backgroundColor: "#1ebe5b", // Slightly darker green on hover
                                    boxShadow: "none", // Prevents hover shadow
                                  },
                                }}
                              >
                                Consultar por whatsapp
                              </Button>
                            </Grid>
                          </>
                        )}
                        {order.type === "click_collect" && (
                          <>
                            <Grid item xs={5}>
                              <Typography>Punto de Retiro</Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography>
                                {order.warehouse?.name ?? "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography>Dirección de Retiro</Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography>
                                {order.warehouse?.address
                                  ? get_full_address(order.warehouse?.address)
                                  : "-"}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </div>
          <div className="tracking-card" align="left">
            <h4 className="title-login fw-medium">Historial del pedido</h4>
            {/* form antigui */}
            <Grid item xs={7}>
              {/* Información de Productos */}
              <Grid
                container
                rowSpacing={2}
                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
              >
                <Grid item xs={12}></Grid>

                {/* Información del Destinatario */}
                <Grid item xs={12}>
                  <Card variant="none">
                    <CardContent>
                      <Grid
                        container
                        rowSpacing={1}
                        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                      >
                        <Timeline style={{ marginRight: "100px" }}>
                          {filteredStatuses.map((status) => (
                            <TimelineItem key={status.date}>
                              <TimelineSeparator>
                                <TimelineDot
                                  style={{ backgroundColor: "#00e1be" }}
                                />
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent>
                                <Typography>
                                  {strDateFormatWithTime(new Date(status.date))}
                                </Typography>
                                <Typography variant="h7">
                                  {status.friendlyStatus}{" "}
                                  {status.friendlySubStatus === "" ? "" : ":"}{" "}
                                  {status.friendlySubStatus}{" "}
                                </Typography>
                                <Typography>{status.details}</Typography>

                                {/* Thumbnails Section */}
                                <Box
                                  display="flex"
                                  flexWrap="wrap"
                                  gap={1}
                                  mt={1}
                                >
                                  {status.image_urls &&
                                    status.image_urls.length > 0 &&
                                    status.image_urls.map((url, index) => (
                                      <img
                                        key={index}
                                        src={url}
                                        alt={`status-thumbnail-${index}`}
                                        style={{
                                          width: 40,
                                          height: 40,
                                          objectFit: "cover",
                                          borderRadius: 4,
                                          border: "1px solid #ddd",
                                          cursor: "pointer", // Indicate the image is clickable
                                        }}
                                        onClick={() => handleImageClick(url)} // Call the function with the clicked URL
                                      />
                                    ))}
                                </Box>
                              </TimelineContent>
                            </TimelineItem>
                          ))}
                        </Timeline>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </div>
        </div>

        {/* Modal for Detailed Image View */}
        <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md">
          <DialogContent>
            {selectedImage && (
              <Box
                component="img"
                src={selectedImage}
                alt="Detailed POD Image"
                sx={{ width: "100%", height: "auto", borderRadius: 1 }}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default TrackOrder;
